/**
 * @author lgalliot (Customer & Monetization)
 *
 * Yup-style validators
 *
 * Useful for formik validationSchema, but warning, they
 * are re-validated for each change no matter which field
 * has been changed. So it consumes a lot. For a per-field
 * validaton take a look at:
 * @see FormField - @components/Form/FormField.tsx -> a shorthand
 *   using useField to prefer local validation instead of global
 *   one. It's based on my understanding of the following link:
 * @see https://formik.org/docs/guides/validation
 * @see useValidators - ./useFieldValidators.ts -> takes the
 *   bellow yup validators and transform them to onChange callbacks.
 */
import {
	MAX_AGE,
	MAX_CODE_LENGTH,
	MIN_CODE_LENGTH,
	MIN_LEGAL_AGE,
	PASSWORD_MAX_LENGTH,
	PASSWORD_MIN_LENGTH,
	USERNAME_MAX_LENGTH,
	USERNAME_MIN_LENGTH,
} from '@app/modules/yup/constants';
import {useMemo} from 'react';
import type {BooleanSchema, NumberSchema, StringSchema} from 'yup';
import {boolean, number, string} from 'yup';
import '@app/modules/yup/addMethods';
import {useTranslation} from './useTranslation';

const EMAIL_FORMAT_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Checks email format */
export const useEmailSchema = (): readonly [StringSchema] => {
	const t = useTranslation();
	const emailSchema = useMemo(
		() =>
			string()
				.transform((value, originalValue) =>
					typeof originalValue === 'string' ? originalValue.trim() : value,
				)
				.matches(EMAIL_FORMAT_REGEX, t('formal.emailformatnotvalid'))
				.required(t('error.fieldmustnotbeempty')),
		[t],
	);
	return [emailSchema];
};

export type PasswordSchema = {
	context: 'reg' | 'login';
};
export const usePasswordSchema = (
	options?: {context?: string}, // Adjust the type of options as needed
): readonly [StringSchema] => {
	const minValue = options?.context == 'reg' ? PASSWORD_MIN_LENGTH : 6;
	const t = useTranslation();

	let schema = string()
		.noTrailingWhiteSpaces(t('formal.passwordnbeginendwithspace'))
		.required(t('error.fieldmustnotbeempty'))
		.min(
			minValue,
			t('formal.passwordnotenoughchars', {
				count: minValue,
				sprintf: [minValue],
			}),
		)
		.max(
			PASSWORD_MAX_LENGTH,
			t('formal.passwordtoomanychars', {
				count: PASSWORD_MAX_LENGTH,
				sprintf: [PASSWORD_MAX_LENGTH],
			}),
		);

	// Must contain at least one letter and one number for registration only
	if (options?.context === 'reg') {
		schema = schema.matches(
			/^(?=.*[a-zA-Z])(?=.*\d).+$/,
			t('password.tooweak'),
		);
	}

	return [schema];
};

export const useAgeSchema = (): readonly [NumberSchema] => {
	const t = useTranslation();
	return [
		number()
			.required(t('error.fieldmustnotbeempty'))
			.min(
				MIN_LEGAL_AGE,
				t('formal.error_agemustbeXyearsentervaliddate', {
					sprintf: [MIN_LEGAL_AGE],
					count: MIN_LEGAL_AGE,
				}),
			)
			.max(MAX_AGE, t('error.ageisinvalid')),
	];
};

export const useConsentSchema = (): readonly [BooleanSchema] => {
	const t = useTranslation();
	return [
		boolean()
			.oneOf([true], t('error.turkeyconsenterror'))
			.required(t('error.turkeyconsenterror')),
	];
};

export const useUsernameSchema = (): readonly [StringSchema] => {
	const t = useTranslation();
	return [
		string()
			.required(t('error.fieldmustnotbeempty'))
			.min(
				USERNAME_MIN_LENGTH,
				t('formal.usernamenotenoughchars', {
					sprintf: [USERNAME_MIN_LENGTH],
					count: USERNAME_MIN_LENGTH,
				}),
			)
			.max(
				USERNAME_MAX_LENGTH,
				t('formal.usernametoomanychars', {
					sprintf: [USERNAME_MAX_LENGTH],
					count: USERNAME_MAX_LENGTH,
				}),
			)
			.hasSpecialCharacters(t('formal.usernamecannotcontainsemojis')),
	];
};

export const useIdentitySchema = (): readonly [StringSchema] => {
	const t = useTranslation();
	return [string().required(t('formal.selectyouridentity'))];
};

export const usePhoneNumberSchema = (): readonly [StringSchema] => {
	const t = useTranslation();
	return [string().required(t('error.fieldmustnotbeempty'))];
};

export const usePinCodeSchema = (): readonly [StringSchema] => {
	const t = useTranslation();
	return [
		string()
			.min(MIN_CODE_LENGTH, t('error.codetooshort'))
			.max(MAX_CODE_LENGTH, t('error.codetoolong'))
			.required(t('error.fieldmustnotbeempty')),
	];
};
